import type { OperationResult } from '@urql/vue'
import Fuse from 'fuse.js'

export type OfflineSearchResult = {
  title: string
  url: string
} & (
  | {
      type: 'medication'
      content: Array<string>
    }
  | {
      type: 'active_ingredient'
      disruptive_effects: Array<string>
      enzyme_induction: Array<string>
      regulations: Array<string>
      miscellaneous: Array<string>
      period: Array<string>
      shorthand: string
    }
  | {
      type: 'general_information'
      content: Array<string>
    }
)

export const FuseInjectionKey: InjectionKey<
  Ref<Fuse<Record<string, any>> | undefined>
> = Symbol('Fuse')

function strip(html: string) {
  let doc = new DOMParser().parseFromString(html, 'text/html')

  return Array.from(doc.body.children[0]?.children ?? [])
    .map((child: Element) => child.textContent?.trim())
    .filter((x) => x)
}

function getComponentText(components: any[]) {
  return components
    .map((component: any) => strip(component?.text ?? ''))
    .filter((x) => x)
    .flat()
}

export const useInjectableFuse = (results: Record<string, any>) => {
  const medicationKeys = Object.keys(results).filter((key) =>
    key.startsWith('med_'),
  )
  const medications = medicationKeys
    .map((key) => results[key])
    .map(
      (entry) =>
        ({
          type: 'medication',
          title: entry?.title ?? '',
          content: strip(entry?.content?.code ?? ''),
        }) as OfflineSearchResult,
    )

  const activeIngredientKeys = Object.keys(results).filter((key) =>
    key.startsWith('aing_'),
  )
  const activeIngredients = activeIngredientKeys
    .map((key) => results[key])
    .map(
      (entry) =>
        ({
          type: 'active_ingredient',
          title: entry?.title ?? '',
          disruptive_effects: strip(entry?.disruptive_effects ?? ''),
          enzyme_induction: strip(entry?.enzyme_induction ?? ''),
          regulations: strip(entry?.regulations ?? ''),
          miscellaneous: strip(entry?.miscellaneous ?? ''),
          period: strip(entry?.period ?? ''),
          shorthand: entry?.shorthand ?? '',
        }) as OfflineSearchResult,
    )

  const mainPages =
    results.generalInformation.structure.tree.map(
      (entry: any) =>
        ({
          type: 'general_information',
          title: entry?.page?.title ?? '',
          url: entry?.page?.url ?? '',
          content: getComponentText(entry?.page?.main_components ?? []),
        }) as OfflineSearchResult,
    ) ?? []
  const subPages =
    results.generalInformation.structure.tree
      .map((entry: any) => entry.children)
      .flat()
      .map(
        (entry: any) =>
          ({
            type: 'general_information',
            title: entry?.page?.title ?? '',
            url: entry?.page?.url ?? '',
            content: getComponentText(entry?.page?.main_components ?? []),
          }) as OfflineSearchResult,
      ) ?? []

  return new Fuse<OfflineSearchResult>(
    [...medications, ...activeIngredients, ...mainPages, ...subPages],
    {
      keys: [
        'title',
        'content',
        'disruptive_effects',
        'enzyme_induction',
        'regulations',
        'miscellaneous',
        'period',
        'shorthand',
      ],
      shouldSort: true,
      includeMatches: true,
      minMatchCharLength: 3,
      ignoreLocation: true,
      threshold: 0.3,
    },
  )
}
